<template>
  <div class="cf-container">
    <div>
      <button
        type="button"
        class="btn btn-success btn-sm m-r-5 m-b-15"
        @click="createCallFlow"
      >
        <i class="fa fa-plus" />
        <span v-t="'BUTTON_CREATE_CALL_FLOW'" />
      </button>
      <Dropdown
        ref="dropdown"
        tag="div"
        :menu-right="true"
      >
        <button class="btn btn-success btn-sm m-b-15">
          Create Using Template
          <i
            class="fa fa-angle-down"
            aria-hidden="true"
          />
        </button>
        <template slot="dropdown">
          <li
            v-for="(template, index) in availableTemplates"
            :key="index"
          >
            <a
              :href="`#/dashboard/call-flow/template/${template.id}`"
              :title="template.description"
              @click="createCallFlowTemplate(template, $event)"
            >
              {{ template.name }}
            </a>
          </li>
        </template>
      </Dropdown>
    </div>
    <!-- Table Container -->
    <div
      v-if="!loading"
      class="grid_table_holder"
    >
      <v-table
        :data="callFlowTrees"
        :current-page.sync="currentPage"
        :filters="filters"
        :page-size="20"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />

            <v-th
              default-sort="asc"
              sort-key="isFinalized"
              class="col-sm-1"
            >
              Status
            </v-th>
            
            <v-th
              default-sort="asc"
              sort-key="version"
              class="col-sm-1"
            >
              Version
            </v-th>
            <v-th
              default-sort="asc"
              sort-key="name"
              class="col-sm-8"
            >
              Name
            </v-th>
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <!-- Rows -->
          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == row.id)"
              :key="row.id"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0,
              }"
            >
              <!-- Mobile Actions -->
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  @click="displayMobileAction(row.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>

              <!-- Status -->
              <td>
                <span
                  class="label"
                  :class="{
                    'label-default': !isEnabled(row),
                    'label-success': isEnabled(row),
                  }"
                >
                  {{ isEnabled(row) ? $t("STATUS_LIVE") : $t("STATUS_DRAFT") }}
                </span>
              </td>
              <!-- Version -->
              <td>
                <span>
                  {{ isLegacy(row) ? $t("VERSION_LEGACY") : $t("VERSION_CALL_FLOW") }}
                  <i
                    v-if="isLegacy(row)"
                    v-tooltip.top.hover="$t('VERSION_LEGACY_TOOLTIP')"
                    class="fa fa-question-circle m-l-5"
                    aria-hidden="true"
                  />
                </span>
              </td>
              <!-- Name -->
              <td>
                <a
                  :href="`#/dashboard/call-flow/edit/${row.id}`"
                  @click="editCallFlow(row.id, $event)"
                >
                  {{ row.name }}
                </a>
              </td>

              <!-- Desktop Actions -->
              <td v-if="!isMobile">
                <CallFlowActions
                  :current-call-flow="row"
                  :duplicate-id="callFlowTreeToDuplicate"
                  @editCallFlow="editCallFlow"
                  @duplicateCallFlow="duplicateCallFlow"
                  @collapse-actions="hideMobileAction()"
                  @deleteCallFlow="confirmDeleteCallFlow"
                />
              </td>
            </tr>

            <tr
              v-if="isMobile && activeActionRowId == row.id"
              :key="row.id + '-mobile'"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <CallFlowActions
                  :current-call-flow="row"
                  :duplicate-id="callFlowTreeToDuplicate"
                  @editCallFlow="editCallFlow"
                  @duplicateCallFlow="duplicateCallFlow"
                  @collapse-actions="hideMobileAction()"
                  @deleteCallFlow="confirmDeleteCallFlow"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
      <div class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="7"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'CALL_FLOWS_TABLE_LOADING'" />
    </div>
    <CallFlowDeleteModal
      v-if="showConfirmation"
      :call-flow-tree="callFlowTreeToDelete"
      :disable-modal-confirm="disableModalConfirm"
      :deleting-call-flow-tree="deletingCallFlowTree"
      :loading-call-flow-tree-references="loadingCallFlowTreeReferences"
      :call-flow-tree-references="callFlowTreeReferences"
      :has-references="hasReferences"
      :call-flow-delete-force.sync="callFlowTreeDeleteForce"
      @closeModal="closeModal"
      @attestDelete="attestDelete"
      @deleteCallFlow="deleteCallFlow"
    />
  </div>
</template>

<script>
import messages from '../../locales';
import sharedMessages from '@/core/locales';
import { mapState, mapGetters, mapActions } from 'vuex';
import { isMobile } from '@/core/utilities';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { $ngStateService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_PENDING, STATUS_SUCCESS, STATUS_ERROR } from '@/store/helpers';

import Loader from '@/core/components/Loader';
import CallFlowActions from './CallFlowActions';
import CallFlowDeleteModal from './CallFlowDeleteModal';

export default {
  name: 'CallFlowsView',
  components: {
    Loader,
    CallFlowActions,
    CallFlowDeleteModal,
  },
  props: {
    filters: {
      type: Object,
      default: {},
    },
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      isMobile: isMobile,
      activeActionRowId: null,
      showConfirmation: false,
      callFlowTreeReferences: null,
      callFlowTreeToDelete: null,
      callFlowTreeDeleteForce: false,
      callFlowTreeToDuplicate: '',
    };
  },
  created() {
    this.getCallFlowTrees();
    this.getAvailableTemplates();
  },
  computed: {
    ...mapState('callFlows', ['callFlowTrees', 'availableTemplates']),
    ...mapGetters('callFlows', [
      'getCallFlowTreesStatus',
      'getCallFlowTreeReferencesStatus',
      'getDeleteCallFlowTreeStatus',
    ]),
    loading() {
      return this.getCallFlowTreesStatus.status == STATUS_PENDING;
    },
    loadingCallFlowTreeReferences() {
      return this.getCallFlowTreeReferencesStatus.status == STATUS_PENDING;
    },
    deletingCallFlowTree() {
      return this.getDeleteCallFlowTreeStatus.status == STATUS_PENDING;
    },
    hasReferences() {
      return this.callFlowTreeReferences?.hasReferences;
    },
    disableModalConfirm() {
      return (
        this.loadingCallFlowTreeReferences ||
        this.deletingCallFlowTree ||
        (!this.callFlowTreeDeleteForce && this.hasReferences)
      );
    },
  },
  methods: {
    ...mapActions('callFlows', [
      'getCallFlowTrees',
      'getAvailableTemplates',
      'duplicateCallFlowTree',
      'getCallFlowTreeReferences',
      'deleteCallFlowTree',
    ]),
    displayMobileAction(rowId) {
      this.activeActionRowId = rowId;
    },
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    createCallFlow() {
      $ngStateService.go('dashboard.call-flow.create');
    },
    async createCallFlowTemplate(template, e) {
      e.preventDefault();
      try {
        $ngStateService.go('dashboard.call-flow.template', {
          templateId: template.id,
        });
      } catch (e) {
        utilities.errorHandler(e);
      }
    },
    editCallFlow(id, e) {
      e.preventDefault();
      $ngStateService.go('dashboard.call-flow.edit', { treeId: id });
    },
    async duplicateCallFlow(id) {
      try {
        this.callFlowTreeToDuplicate = id;
        const newCallFlow = await this.duplicateCallFlowTree(id);
        $ngStateService.go('dashboard.call-flow.edit', {
          treeId: newCallFlow.id,
        });
      } catch (e) {
        this.callFlowTreeToDuplicate = '';
        utilities.errorHandler(e);
      }
    },
    async confirmDeleteCallFlow(callFlow) {
      this.callFlowTreeToDelete = callFlow;
      this.showConfirmation = true;
      try {
        const callFlowTreeReferences = await this.getCallFlowTreeReferences(
          callFlow.id
        );
        this.callFlowTreeReferences = callFlowTreeReferences;
      } catch {
        // error handled in store
      }
    },
    async deleteCallFlow(callFlow) {
      const payload = {
        treeId: callFlow.id,
        force: this.callFlowTreeDeleteForce,
      };
      await this.deleteCallFlowTree(payload);
      if (this.getDeleteCallFlowTreeStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(
          STATUS_SUCCESS,
          this.$i18n.t('DELETE_CALL_FLOW_SUCCESS_MESSAGE')
        );
        this.getCallFlowTrees();
      }
      if (this.getDeleteCallFlowTreeStatus.status === STATUS_ERROR) {
        utilities.showToaster(
          STATUS_ERROR,
          this.deleteCallFlowTreeStatus.error
        );
      }
      this.showConfirmation = false;
    },
    attestDelete(value) {
      this.callFlowTreeDeleteForce = value;
    },
    closeModal() {
      this.showConfirmation = false;
    },
    isEnabled(callflow) {
      return callflow.isFinalized;
    },
    isLegacy(callflow) {
      return callflow.version == 1;
    },
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>